import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import Modal from './Modal';
import { AppTextField } from '../customComponents/AppTextField';
import { AppDropdownField } from '../customComponents/AppDropdownField';
import { AppGPhoneTextField } from '../customComponents/AppGPhoneTextField';
import { FormTitle } from '../customComponents/FormTitle';
import PrimaryButton from './PrimaryButton';
import Agreement from './Agreement';
import TermsConsentSheet from './TermsConsentSheet';
import { updateUserInfo, getTerms, getUserInfo } from '../lib/api/user/userApi';
import { useUserAuth } from '../context/UserAuthContext';
import { useLang } from '../context/LangContext';
import { devLog, devError } from '../utils/devLogger';

interface AdditionalInfoInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleted?: () => void; // 저장 완료 후 호출 (예: 화면 리프레시 등)
}

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SubtitleText = styled.p`
  font-size: 14px;
  color: #666;
  text-align: left;
  margin: 0 0 8px 0;
  line-height: 1.4;
`;

const AgreeContainer = styled.div`
  margin-top: 8px;
`;

const additionalInfoTexts = {
  ko: {
    title: '추가 정보 입력',
    subtitle: '서비스 이용을 위해 추가 정보를 입력해주세요',
    updateSuccess: '추가 정보가 저장되었습니다!',
    updateFailed: '정보 저장에 실패했습니다. 다시 시도해주세요.',
    termsLoadFailed: '약관을 불러오는데 실패했습니다.',
    nameValidationError: '이름은 2글자 이상 입력해주세요',
    countryValidationError: '국가를 선택해주세요',
    completeButton: '완료'
  },
  en: {
    title: 'Additional Information',
    subtitle: 'Please enter additional information to use the service',
    updateSuccess: 'Additional information saved successfully!',
    updateFailed: 'Failed to save information. Please try again.',
    termsLoadFailed: 'Failed to load terms.',
    nameValidationError: 'Please enter at least 2 characters for name',
    countryValidationError: 'Please select country',
    completeButton: 'Complete'
  }
};

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const CancelButton = styled.button`
  flex: 1;
  height: 48px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background-color: #fff;
  font-size: 15px;
  cursor: pointer;
`;

const ConfirmButtonWrapper = styled.div`
  flex: 1;
`;

const AdditionalInfoInputModal: React.FC<AdditionalInfoInputModalProps> = ({
  isOpen,
  onClose,
  onCompleted
}) => {
  const { user, login } = useUserAuth();
  const { lang } = useLang();
  const texts = additionalInfoTexts[lang];

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [isNameValid, setIsNameValid] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isCountryValid, setIsCountryValid] = useState(false);
  const [individualAgreements, setIndividualAgreements] = useState<boolean[]>([false, false]);
  const [isTermsSheetOpen, setIsTermsSheetOpen] = useState(false);
  const [currentTermsSection, setCurrentTermsSection] = useState('privacy');
  const [termsSection, setTermsSection] = useState<any[]>([]);
  const [isLoadingTerms, setIsLoadingTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // 모달이 열릴 때마다 기본 값 세팅
  useEffect(() => {
    if (isOpen) {
      if (user?.name && user.name.trim() !== '') {
        setName(user.name);
        setIsNameValid(user.name.trim().length >= 2);
      } else {
        setName('');
        setIsNameValid(false);
      }
      setPhone('');
      setIsPhoneValid(false);
      setCountry('');
      setIsCountryValid(false);
      setIndividualAgreements([false, false]);
    }
  }, [isOpen, user]);

  // 모달이 열릴 때 서버에서 최신 사용자 정보 조회 후 값 바인딩
  useEffect(() => {
    if (!isOpen) return;

    const fetchUserInfo = async () => {
      try {
        const response = await getUserInfo();
        devLog('AdditionalInfoInputModal에서 getUserInfo 호출 결과:', response.data);

        if (response.data?.statusCode === 200 && response.data.data) {
          const userData = response.data.data;

          // 서버에 저장된 이름이 있으면 바인딩
          if (userData.name && userData.name.trim() !== '') {
            setName(userData.name);
            setIsNameValid(userData.name.trim().length >= 2);
          }

          // 서버에 저장된 연락처가 있으면 바인딩
          if (userData.cellphone && userData.cellphone.trim() !== '') {
            setPhone(userData.cellphone);
            setIsPhoneValid(true);
          }

          // 서버에 저장된 국가(locationType)가 있으면 바인딩
          if (userData.locationType === 'DOMESTIC') {
            setCountry('domestic');
            setIsCountryValid(true);
          } else if (userData.locationType === 'OVERSEAS') {
            setCountry('overseas');
            setIsCountryValid(true);
          }
        }
      } catch (error) {
        devError('AdditionalInfoInputModal에서 getUserInfo 호출 실패:', error);
      }
    };

    fetchUserInfo();
  }, [isOpen]);

  // 약관 조회
  useEffect(() => {
    if (!isOpen) return;

    const fetchTerms = async () => {
      try {
        setIsLoadingTerms(true);
        const termLang = lang === 'ko' ? 'KOR' : 'ENG';
        const response = await getTerms({ termLang });

        if (response.data.statusCode === 200 && response.data.data) {
          const termsArray = response.data.data as any[];

          let privacyIndex, serviceIndex;
          if (lang === 'ko') {
            privacyIndex = termsArray.find((term: any) => term.index === 1);
            serviceIndex = termsArray.find((term: any) => term.index === 3);
          } else {
            privacyIndex = termsArray.find((term: any) => term.index === 2);
            serviceIndex = termsArray.find((term: any) => term.index === 4);
          }

          const formattedTerms = [
            {
              key: 'privacy',
              title: lang === 'ko' ? '개인정보\n취급방침' : 'Privacy\nPolicy',
              content: privacyIndex?.content || ''
            },
            {
              key: 'terms',
              title: lang === 'ko' ? '서비스\n이용약관' : 'Terms of\nService',
              content: serviceIndex?.content || ''
            }
          ];

          setTermsSection(formattedTerms);
        }
      } catch (error) {
        devError('약관 조회 실패:', error);
        toast.error(texts.termsLoadFailed);
      } finally {
        setIsLoadingTerms(false);
      }
    };

    fetchTerms();
  }, [isOpen, lang, texts.termsLoadFailed]);

  const countryOptions = [
    { value: 'domestic', label: lang === 'ko' ? '국내' : 'Domestic' },
    { value: 'overseas', label: lang === 'ko' ? '국외' : 'Overseas' },
  ];

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setName(e.target.value);
    setIsNameValid(e.target.value.trim().length >= 2);
  };

  const handlePhoneChange = (value: string, valid: boolean) => {
    setPhone(value);
    setIsPhoneValid(valid);
  };

  const handleCountryChange = (value: string) => {
    setCountry(value);
    setIsCountryValid(value !== '');
  };

  const handleAgreementChange = (_allAgreed: boolean, agreements: boolean[]) => {
    setIndividualAgreements(agreements);
  };

  const handleViewPrivacyPolicy = () => {
    setCurrentTermsSection('privacy');
    setIsTermsSheetOpen(true);
  };

  const handleViewTerms = () => {
    setCurrentTermsSection('terms');
    setIsTermsSheetOpen(true);
  };

  const handleTermsSheetClose = () => {
    setIsTermsSheetOpen(false);
  };

  const handleTermsSectionClick = (sectionKey: string) => {
    setCurrentTermsSection(sectionKey);
  };

  const handleComplete = async () => {
    if (isLoading || !user) return;

    try {
      setIsLoading(true);
      devLog('추가 정보 업데이트 시작 (모달):', {
        name,
        phone,
        country,
        userId: user.userId,
      });

      const response = await updateUserInfo({
        name: name.trim(),
        cellphone: phone,
        locationType: country === 'domestic' ? 'DOMESTIC' : 'OVERSEAS',
      });

      if (response.data.statusCode === 200) {
        toast.success(texts.updateSuccess);

        const updatedUser = {
          ...user,
          name: name.trim(),
          needMoreInfo: false,
          locationType: country === 'domestic' ? 'DOMESTIC' : 'OVERSEAS',
        };

        login(updatedUser, localStorage.getItem('accessToken') || '');

        if (onCompleted) {
          onCompleted();
        }

        onClose();
      } else {
        let errorMessage = texts.updateFailed;
        const errorData = response.data;

        if (errorData?.error?.customMessage) {
          errorMessage = errorData.error.customMessage;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }

        toast.error(errorMessage);
      }
    } catch (error: any) {
      devError('추가 정보 업데이트 실패 (모달):', error);
      toast.error(texts.updateFailed);
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid =
    isNameValid &&
    isPhoneValid &&
    isCountryValid &&
    individualAgreements.every((agreed) => agreed) &&
    name.trim() !== '' &&
    phone.trim() !== '' &&
    country.trim() !== '';

  const agreeContents = [
    {
      title:
        lang === 'ko'
          ? '(필수) 개인정보 취급방침 동의'
          : '(Required) Privacy Policy Agreement',
      onView: handleViewPrivacyPolicy,
    },
    {
      title:
        lang === 'ko'
          ? '(필수) 서비스 이용약관 수집 동의'
          : '(Required) Terms of Service Agreement',
      onView: handleViewTerms,
    },
  ];

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        bgColor="white"
        width="360px"
        padding="20px 16px 16px 16px"
        header={texts.title}
        body={
          <ContentContainer>
            <SubtitleText>{texts.subtitle}</SubtitleText>

            <FormTitle>{lang === 'ko' ? '회원정보 입력' : 'Enter Member Information'}</FormTitle>

            <AppTextField
              value={name}
              onChange={handleNameChange}
              label={lang === 'ko' ? '이름' : 'Name'}
              placeholder={lang === 'ko' ? '이름을 입력해주세요' : 'Please enter your name'}
              errorMessage={name && !isNameValid ? texts.nameValidationError : ''}
              autoComplete="name"
            />

            <AppGPhoneTextField value={phone} onChange={handlePhoneChange} language={lang} />

            <AppDropdownField
              value={country}
              onChange={handleCountryChange}
              options={countryOptions}
              label={lang === 'ko' ? '국가' : 'Country'}
              placeholder={lang === 'ko' ? '국가를 선택해주세요' : 'Please select country'}
              errorMessage={country && !isCountryValid ? texts.countryValidationError : ''}
              language={lang}
            />

            <AgreeContainer>
              <Agreement
                agreeContents={agreeContents}
                onAgreementChange={handleAgreementChange}
                initialAgreements={individualAgreements}
              />
            </AgreeContainer>
          </ContentContainer>
        }
        footer={
          <ButtonRow>
            <CancelButton onClick={onClose}>
              {lang === 'ko' ? '취소' : 'Cancel'}
            </CancelButton>
            <ConfirmButtonWrapper>
              <PrimaryButton onClick={handleComplete} disabled={!isFormValid || isLoading}>
                {isLoading ? (lang === 'ko' ? '저장 중...' : 'Saving...') : texts.completeButton}
              </PrimaryButton>
            </ConfirmButtonWrapper>
          </ButtonRow>
        }
      />

      {isTermsSheetOpen && (
        <TermsConsentSheet
          sections={termsSection}
          viewedSection={currentTermsSection}
          onClose={handleTermsSheetClose}
          onSectionClick={handleTermsSectionClick}
          isLoading={isLoadingTerms}
        />
      )}
    </>
  );
};

export default AdditionalInfoInputModal;
